import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import './SharedStyles.css';
import './DocumentLibrary.css';
import DocumentUploadModal from './DocumentUploadModal';

interface Document {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  status: string;
  metadata?: any;
}

interface QueryResult {
  chunk_text: string;
  similarity_score: number;
  source_document: {
    filename: string;
    document_id: string;
  };
  chunk_index: number;
  metadata: any;
}

interface QueryResponse {
  query: string;
  results: QueryResult[];
  total_results: number;
}

const DocumentLibrary: React.FC = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState<'upload' | 'testing'>('upload');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // RAG Query Tester state
  const [query, setQuery] = useState('');
  const [nResults, setNResults] = useState(5);
  const [ragLoading, setRagLoading] = useState(false);
  const [ragResults, setRagResults] = useState<QueryResult[]>([]);
  const [ragError, setRagError] = useState<string | null>(null);
  const [expandedChunks, setExpandedChunks] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/rag/documents`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data.documents || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setDeletingId(documentId);
      const response = await fetch(`${API_URL}/api/rag/documents/${documentId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      // Remove from list
      setDocuments(documents.filter(doc => doc.id !== documentId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'indexed':
        return '#7A6B35'; // brand color
        case 'processing':
        return '#f59e0b'; // yellow
        case 'error':
        return '#ef4444'; // red
        default:
        return '#6b7280'; // gray
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchDocuments();
  };

  // RAG Query Tester functions
  const handleSearch = async () => {
    if (!query.trim()) {
      setRagError('Please enter a query');
      return;
    }

    setRagLoading(true);
    setRagError(null);
    setRagResults([]);

    try {
      const response = await fetch(`${API_URL}/api/rag/test-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
          query: query.trim(),
          n_results: nResults,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Query failed');
      }

      const data: QueryResponse = await response.json();
      setRagResults(data.results || []);
    } catch (err) {
      setRagError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setRagLoading(false);
    }
  };

  const toggleChunkExpansion = (index: number) => {
    const newExpanded = new Set(expandedChunks);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedChunks(newExpanded);
  };

  const getSimilarityColor = (score: number): string => {
    if (score >= 0.7) return '#7A6B35'; // brand color
    if (score >= 0.5) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const formatSimilarity = (score: number): string => {
    return (score * 100).toFixed(1) + '%';
  };

  const truncateText = (text: string, maxLength: number = 200): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="glass-card loading-card">
            <div className="loading-content">
              <div className="loading-logo">
                <div className="logo-container">
                  <img src="/icon.png" alt="Forensic AI Logo" className="logo" />
                </div>
                <h2 className="loading-title">Forensic AI</h2>
              </div>
              <p className="loading-text">Loading documents...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <Navigation />
      <div className="documents-container">
        {/* Hero/Intro Section */}
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-left">
              <h1 className="hero-title">
                Document Library
              </h1>
            </div>
            <div className="hero-right">
              <p className="hero-description">
                Manage your document collection for AI-powered search and analysis. Upload company literature, 
                policies, and documents to enable intelligent RAG (Retrieval-Augmented Generation) queries with 
                semantic search capabilities.
              </p>
            </div>
          </div>
          <div className="hero-pattern"></div>
        </div>

        {/* Content Card */}
        <div className="documents-content-card">
          {/* Tab Switcher */}
          <div className="tab-switcher">
            <div style={{ display: 'flex', gap: '0.75rem', flex: 1 }}>
              <button
                onClick={() => setActiveTab('upload')}
                className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
              >
                <i className="fas fa-file-alt" style={{ marginRight: '0.5rem' }}></i>
                Documents
              </button>
              <button
                onClick={() => setActiveTab('testing')}
                className={`tab-button ${activeTab === 'testing' ? 'active' : ''}`}
              >
                <i className="fas fa-search" style={{ marginRight: '0.5rem' }}></i>
                Testing
              </button>
            </div>
            <button
              className="add-document-btn"
              onClick={() => setShowUploadModal(true)}
              title="Upload Document"
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>

      {/* Upload Tab Content */}
      {activeTab === 'upload' && (
        <>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
              {error}
            </div>
          )}

          {documents.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                No documents uploaded yet.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowUploadModal(true)}
              >
                Upload Your First Document
              </button>
            </div>
          ) : (
            <div className="card">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div className="filename-cell">{doc.filename}</div>
                      </td>
                      <td className="meta-cell">
                        {doc.file_type?.toUpperCase() || 'N/A'}
                      </td>
                      <td className="meta-cell">
                        {formatFileSize(doc.file_size || 0)}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusColor(doc.status) + '20',
                            color: getStatusColor(doc.status),
                          }}
                        >
                          {doc.status}
                        </span>
                      </td>
                      <td className="meta-cell">
                        {formatDate(doc.uploaded_at)}
                      </td>
                      <td>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDelete(doc.id)}
                          disabled={deletingId === doc.id}
                          style={{
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.875rem',
                          }}
                        >
                          {deletingId === doc.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Testing Tab Content */}
      {activeTab === 'testing' && (
        <>
          <div className="card" style={{ marginBottom: '2rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="query"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                }}
              >
                Query
              </label>
              <textarea
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your search query..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-end',
                marginBottom: '1rem',
              }}
            >
              <div style={{ flex: '1' }}>
                <label
                  htmlFor="nResults"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                  }}
                >
                  Number of Results
                </label>
                <input
                  id="nResults"
                  type="number"
                  min="1"
                  max="20"
                  value={nResults}
                  onChange={(e) => setNResults(parseInt(e.target.value) || 5)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                  }}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={ragLoading || !query.trim()}
                style={{ padding: '0.5rem 1.5rem' }}
              >
                {ragLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {ragError && (
              <div className="alert alert-error" style={{ marginTop: '1rem' }}>
                {ragError}
              </div>
            )}
          </div>

          {ragResults.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '1rem' }}>
                Results ({ragResults.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {ragResults.map((result, index) => (
                  <div
                    key={index}
                    className="card"
                    style={{
                      borderLeft: `4px solid ${getSimilarityColor(result.similarity_score)}`,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                      }}
                    >
                      <div style={{ flex: '1' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              backgroundColor:
                                getSimilarityColor(result.similarity_score) + '20',
                              color: getSimilarityColor(result.similarity_score),
                            }}
                          >
                            {formatSimilarity(result.similarity_score)}
                          </span>
                          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            Chunk #{result.chunk_index}
                          </span>
                        </div>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                          {result.source_document.filename}
                        </div>
                        <div
                          style={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            fontFamily: 'monospace',
                          }}
                        >
                          Document ID: {result.source_document.document_id.substring(0, 8)}...
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(result.chunk_text);
                        }}
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.875rem',
                        }}
                        title="Copy chunk text"
                      >
                        📋 Copy
                      </button>
                    </div>

                    <div
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0.375rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div style={{ marginBottom: '0.5rem' }}>
                        {expandedChunks.has(index) ? (
                          <div style={{ whiteSpace: 'pre-wrap' }}>
                            {result.chunk_text}
                          </div>
                        ) : (
                          <div>{truncateText(result.chunk_text)}</div>
                        )}
                      </div>
                      <button
                        className="btn btn-link"
                        onClick={() => toggleChunkExpansion(index)}
                        style={{
                          padding: '0',
                          fontSize: '0.875rem',
                          color: '#3b82f6',
                        }}
                      >
                        {expandedChunks.has(index) ? 'Show Less' : 'Show More'}
                      </button>
                    </div>

                    {result.metadata && Object.keys(result.metadata).length > 0 && (
                      <details style={{ marginTop: '1rem' }}>
                        <summary
                          style={{
                            cursor: 'pointer',
                            color: '#6b7280',
                            fontSize: '0.875rem',
                          }}
                        >
                          View Metadata
                        </summary>
                        <pre
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.75rem',
                            backgroundColor: '#f9fafb',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(result.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!ragLoading && ragResults.length === 0 && query && (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: '#6b7280' }}>No results found for your query.</p>
            </div>
          )}
        </>
      )}

      {showUploadModal && (
        <DocumentUploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
        </div>
      </div>
    </div>
  );
};

export default DocumentLibrary;




